import React, { useState, useMemo, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { formations as mockFormations } from '../mock/mockData';
import { fetchFormations } from '../lib/api';
import { Link } from 'react-router-dom';
import { Search, Clock, Award, TrendingUp, SlidersHorizontal } from 'lucide-react';
import { cleanFormationTitle, isProgrammeTag } from '../lib/formationDisplay';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const typeColors = {
  'E-Learning': 'bg-sky-50 text-sky-700 border-sky-200',
  'Présentiel': 'bg-amber-50 text-amber-700 border-amber-200',
  'Mixte': 'bg-purple-50 text-purple-700 border-purple-200',
  'Hybride': 'bg-violet-50 text-violet-800 border-violet-200',
  'Classe Virtuelle': 'bg-cyan-50 text-cyan-800 border-cyan-200',
  'Évaluation Pratique': 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const CATEGORY_PRE_ORDER = [
  'Médecin Généraliste',
  'Sage-femme',
  "Infirmier diplômé d'État",
  'IDEL',
  'Kinésithérapeute',
  'Chirurgien-Dentiste',
  'Dentaire',
  'Pédiatre',
  'Psychiatre',
  'Ophtalmologiste',
  'Anesthésiste-réanimateur',
  'Pharmacie',
  'Aide-Soignant',
  'Gériatrie',
];

function sortCategoryLabels(cats) {
  const idx = (c) => {
    const i = CATEGORY_PRE_ORDER.indexOf(c);
    return i === -1 ? 1000 : i;
  };
  return [...cats].sort((a, b) => (idx(a) - idx(b)) || a.localeCompare(b, 'fr'));
}

const fmtEuro = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(n));
};

const isDev = process.env.NODE_ENV === 'development';

const FormationsPage = () => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('Toutes');
  const [type, setType] = useState('Toutes');
  const [formations, setFormations] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogLoadFailed, setCatalogLoadFailed] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let alive = true;
    setCatalogLoading(true);
    setCatalogLoadFailed(false);
    fetchFormations()
      .then((data) => {
        if (!alive) return;
        if (Array.isArray(data) && data.length > 0) {
          setFormations(data);
        } else if (isDev) {
          setFormations(mockFormations);
        } else {
          setFormations([]);
          setCatalogLoadFailed(true);
        }
      })
      .catch(() => {
        if (!alive) return;
        if (isDev) {
          setFormations(mockFormations);
        } else {
          setFormations([]);
          setCatalogLoadFailed(true);
        }
      })
      .finally(() => {
        if (alive) setCatalogLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [reloadToken]);

  const formationCategories = useMemo(() => {
    const u = new Set();
    formations.forEach((f) => {
      if (f.category) u.add(f.category);
    });
    return ['Toutes', ...sortCategoryLabels([...u])];
  }, [formations]);

  const formationTypes = useMemo(() => {
    const u = new Set();
    formations.forEach((f) => {
      if (f.type) u.add(f.type);
    });
    return ['Toutes', ...[...u].sort((a, b) => a.localeCompare(b, 'fr'))];
  }, [formations]);

  useEffect(() => {
    if (category !== 'Toutes' && !formationCategories.includes(category)) {
      setCategory('Toutes');
    }
  }, [formationCategories, category]);

  useEffect(() => {
    if (type !== 'Toutes' && !formationTypes.includes(type)) {
      setType('Toutes');
    }
  }, [formationTypes, type]);

  const filtered = useMemo(() => {
    const q = (search || '').toLowerCase().trim();
    return formations.filter((f) => {
      const title = (f.title || '').toLowerCase();
      const titleClean = cleanFormationTitle(f.title || '').toLowerCase();
      const code = (f.code || '').toLowerCase();
      const refStr = String(f.id);
      const matchSearch =
        !q
        || title.includes(q)
        || titleClean.includes(q)
        || code.includes(q)
        || refStr.includes(q);
      const matchCat = category === 'Toutes' || f.category === category;
      const matchType = type === 'Toutes' || f.type === type;
      return matchSearch && matchCat && matchType;
    });
  }, [formations, search, category, type]);

  const facetCounts = useMemo(() => {
    const q = (search || '').toLowerCase().trim();
    const counts = { categories: new Map(), types: new Map() };

    const baseMatches = (f) => {
      const title = (f.title || '').toLowerCase();
      const titleClean = cleanFormationTitle(f.title || '').toLowerCase();
      const code = (f.code || '').toLowerCase();
      const refStr = String(f.id);
      const matchSearch =
        !q
        || title.includes(q)
        || titleClean.includes(q)
        || code.includes(q)
        || refStr.includes(q);
      return matchSearch;
    };

    formations.forEach((f) => {
      if (!baseMatches(f)) return;
      const cat = f.category || '';
      const t = f.type || '';
      if (cat) counts.categories.set(cat, (counts.categories.get(cat) || 0) + 1);
      if (t) counts.types.set(t, (counts.types.get(t) || 0) + 1);
    });

    // for type facet, also respect selected category (so user understands empty results)
    const typesWithCategory = new Map();
    formations.forEach((f) => {
      if (!baseMatches(f)) return;
      if (category !== 'Toutes' && f.category !== category) return;
      const t = f.type || '';
      if (t) typesWithCategory.set(t, (typesWithCategory.get(t) || 0) + 1);
    });

    return { ...counts, typesWithCategory };
  }, [formations, search, category]);

  return (
    <div className="min-h-screen bg-slate-50/40">
      <Seo
        title="Catalogue de formations pour professionnels de santé"
        description="Parcourez le catalogue Medelia : formations certifiées Qualiopi, filtres par discipline et format (présentiel, e-learning, mixte). Accompagnement financement DPC, FIFPL, OPCO, CPF."
        canonicalPath="/formations"
      />
      <Navbar />
      <main className="safe-pt-nav">
        <section className="relative bg-gradient-to-br from-teal-50 via-white to-emerald-50/50 py-16 border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <span className="text-teal-600 text-sm font-bold uppercase tracking-widest">Catalogue</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-3 tracking-tight">
              Trouvez la formation qui vous correspond
            </h1>
            <p className="text-slate-600 mt-4 max-w-2xl">Parcourez notre catalogue de formations certifiées pour professionnels de santé. Filtrage par discipline et par format, prise en charge selon éligibilité.</p>

            <div className="mt-8 relative max-w-2xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Rechercher une formation, un code, une discipline..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-12 h-14 bg-white border-slate-200 text-base shadow-md shadow-slate-900/5 focus-visible:ring-teal-500"
              />
            </div>
          </div>
        </section>

        <section className="py-10">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            {catalogLoading && (
              <p className="text-center text-slate-500 py-8 text-sm">Chargement du catalogue…</p>
            )}
            {!catalogLoading && catalogLoadFailed && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50/80 px-6 py-8 text-center mb-8">
                <p className="text-slate-800 font-medium">Le catalogue n’a pas pu être chargé depuis le serveur.</p>
                <p className="text-slate-600 text-sm mt-2 max-w-lg mx-auto">
                  Vérifiez votre connexion, ou que l’API est bien déployée sur le même site (route <code className="text-xs bg-white/80 px-1 rounded border">/api/formations</code>).
                </p>
                <Button className="mt-5 bg-slate-900 hover:bg-teal-600" type="button" onClick={() => setReloadToken((t) => t + 1)}>
                  Réessayer
                </Button>
              </div>
            )}
            {!catalogLoading && !catalogLoadFailed && (
            <>
            <div className="mb-8 space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <SlidersHorizontal className="w-4 h-4" /> Discipline
              </div>
              <div className="flex flex-wrap gap-2">
                {formationCategories.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCategory(c)}
                    className={`min-h-11 px-4 py-2.5 rounded-full text-xs font-semibold border transition-all touch-manipulation ${category === c ? 'bg-teal-600 border-teal-600 text-white shadow-md shadow-teal-500/20' : 'bg-white border-slate-200 text-slate-700 hover:border-teal-300'}`}
                  >
                    {c}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 pt-3">Format</div>
              <div className="flex flex-wrap gap-2">
                {formationTypes.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    disabled={t !== 'Toutes' && (facetCounts.typesWithCategory?.get(t) || 0) === 0}
                    className={`min-h-11 px-4 py-2.5 rounded-full text-xs font-semibold border transition-all touch-manipulation ${
                      type === t
                        ? 'bg-slate-900 border-slate-900 text-white'
                        : (t !== 'Toutes' && (facetCounts.typesWithCategory?.get(t) || 0) === 0)
                          ? 'bg-white border-slate-200 text-slate-300 cursor-not-allowed'
                          : 'bg-white border-slate-200 text-slate-700 hover:border-slate-400'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
                <p className="text-slate-600">Aucune formation ne correspond à votre recherche.</p>
                <Button variant="outline" className="mt-4" onClick={() => { setSearch(''); setCategory('Toutes'); setType('Toutes'); }}>Réinitialiser les filtres</Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filtered.map((f) => (
                  <div key={f.id} className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-teal-400 hover:shadow-xl hover:shadow-teal-500/10 hover:-translate-y-1 transition-all duration-300 flex flex-col">
                    <div className="h-24 bg-gradient-to-br from-teal-600 via-emerald-600 to-teal-700 relative overflow-hidden">
                      <div className="absolute inset-0 opacity-25" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                      {f.popular && (
                        <div className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 rounded-full border border-white/30 bg-white/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white backdrop-blur-sm">
                          <TrendingUp className="w-3 h-3" /> Tendance
                        </div>
                      )}
                      <div className="absolute top-2.5 right-2.5 inline-flex items-center gap-1 rounded-md border border-white/25 bg-white/10 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm">
                        <Award className="w-3 h-3" /> DPC
                      </div>
                    </div>
                    <div className="p-5 flex-1 flex flex-col min-h-0">
                      <div className="flex flex-wrap items-start gap-2 mb-3">
                        <span
                          className={`inline-flex max-w-full items-center rounded-lg border px-2.5 py-1 text-[11px] font-semibold leading-tight ${
                            typeColors[f.type] || 'bg-slate-50 text-slate-700 border-slate-200'
                          }`}
                        >
                          {f.type}
                        </span>
                        <span className="inline-flex max-w-full items-center rounded-lg border border-slate-200 bg-slate-50/90 px-2.5 py-1 text-[11px] font-medium leading-tight text-slate-800">
                          {f.category}
                        </span>
                      </div>
                      <h3 className="text-[15px] font-bold text-slate-900 leading-snug mb-3 line-clamp-3 min-h-[60px] group-hover:text-teal-700 transition-colors break-words">
                        {cleanFormationTitle(f.title)}
                      </h3>
                      <div className="mb-4 flex min-w-0 flex-col gap-2 text-xs text-slate-600">
                        <div className="flex items-center gap-1.5 text-slate-500">
                          <Clock className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                          <span className="font-medium text-slate-700">{f.hours} h</span>
                        </div>
                        {f.tag && (
                          <div>
                            {isProgrammeTag(f.tag) ? (
                              <span className="inline-flex max-w-full rounded-md border border-teal-200 bg-teal-50 px-2 py-1 text-[11px] font-medium leading-snug text-teal-900">
                                {f.tag}
                              </span>
                            ) : (
                              <span className="inline-flex max-w-full rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-medium leading-snug text-slate-700">
                                {f.tag}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="mt-auto pt-4 border-t border-slate-100 flex items-end justify-between">
                        <div>
                          <p className="text-[10px] text-slate-500 uppercase tracking-wide">Prix</p>
                          <p className="text-xl font-bold text-slate-900 tabular-nums">{fmtEuro(f.price)}&nbsp;€</p>
                          {f.indemnite > 0 && (
                            <p className="text-[10px] text-emerald-600 font-semibold">+&nbsp;{fmtEuro(f.indemnite)}&nbsp;€ indemnité</p>
                          )}
                        </div>
                        <Button size="sm" className="bg-slate-900 hover:bg-teal-600 text-white text-xs h-8" asChild>
                          <Link to={`/inscription?formation=${f.id}`}>S'inscrire</Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            </>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default FormationsPage;
